<template>
  <div class="mt-4">
    <Tooltip
      :key="
        t('subscribeToEventUpdatesModal.firstStep.form.dateType.toolTipText')
      "
      :tooltip-text="
        t('subscribeToEventUpdatesModal.firstStep.form.dateType.toolTipText')
      "
      :disabled="!tooltipEnabled"
      length="large"
      type="span"
      class="self-center mb-2 block"
    >
      <label for="date-type-select" class="block mb-2 required">
        {{ t('subscribeToEventUpdatesModal.firstStep.form.dateType.label') }}
      </label>
    </Tooltip>
    <FormSelect
      id="date-type-select"
      v-model="dateType"
      :error="dateTypeError"
      data-test-id="date-type-select"
    >
      <option value="" disabled hidden>
        {{
          t('subscribeToEventUpdatesModal.firstStep.form.dateType.placeholder')
        }}
      </option>
      <option v-if="allowedTypes.includes('PUBLIC')" value="PUBLIC">
        {{ t('subscribeToEventUpdatesModal.firstStep.form.dateType.public') }}
      </option>
      <option v-if="allowedTypes.includes('PRIVATE')" value="PRIVATE">
        {{ t('subscribeToEventUpdatesModal.firstStep.form.dateType.private') }}
      </option>
    </FormSelect>
  </div>
</template>

<script setup lang="ts">
import FormSelect from '~/components/common/form/FormSelect.vue'
import type {
  DateRequestErrors,
  DateRequestInvalidKeys,
} from '~/components/date-request/dateRequestTypes'
import type { EventDateType } from '~/composables/types/event'

interface Props {
  tooltipEnabled?: boolean
  errors?: DateRequestErrors | null
  invalidKeys?: DateRequestInvalidKeys
  /** Types the partner allows; a single entry is preselected. */
  allowedTypes?: EventDateType[]
}

const {
  tooltipEnabled = false,
  errors = {},
  invalidKeys = [],
  allowedTypes = ['PUBLIC', 'PRIVATE'],
} = defineProps<Props>()

const modelValue = defineModel<EventDateType | null>({ required: true })

const emit = defineEmits<{
  (e: 'clear-errors', field: string): void
}>()

const { t } = useI18n()

// Preselect the only allowed type. If the event changes and widens the
// options, drop a value we forced so the user makes the choice themselves.
const forcedType = ref<EventDateType | null>(null)
watch(
  () => allowedTypes.join(','),
  () => {
    const onlyType = allowedTypes.length === 1 ? allowedTypes[0] : undefined
    if (onlyType) {
      forcedType.value = onlyType
      modelValue.value = onlyType
      return
    }
    if (
      (forcedType.value && modelValue.value === forcedType.value) ||
      (modelValue.value && !allowedTypes.includes(modelValue.value))
    ) {
      modelValue.value = null
    }
    forcedType.value = null
  },
  { immediate: true }
)

// Saved form data hydrates after mount and replaces `dateType`, so keep the
// only allowed type selected whenever the value changes underneath us.
watch(modelValue, (value) => {
  const onlyType = allowedTypes.length === 1 ? allowedTypes[0] : undefined
  if (onlyType && value !== onlyType) {
    modelValue.value = onlyType
  }
})

const dateTypeError = computed(() =>
  invalidKeys.includes('dateType')
    ? errors?.dateType || t('general.form.validation.required')
    : undefined
)

const dateType = computed<EventDateType | ''>({
  get: () => modelValue.value ?? '',
  set: (value) => {
    modelValue.value = value === '' ? null : (value as EventDateType)
    emit('clear-errors', 'dateType')
  },
})
</script>

<style lang="postcss" scoped>
.required:after {
  content: '*';
  position: relative;
  font-size: inherit;
  @apply text-red;
  padding-left: 0.25rem;
  font-weight: 700;
}
</style>
